import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Minus, Plus, Trash2, Send, Loader2, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { MTopBar, ComandaBar } from "@/components/mobile/MTopBar";
import { MPage, Footer, EmptyArt, cardCls } from "@/components/mobile/parts";
import { useCartActions } from "@/pages/mobile/MProducts";
import { useComanda } from "@/hooks/useMobileData";
import { useMobile } from "@/context/MobileContext";
import { useAuth } from "@/context/AuthContext";
import { api, brl, errMsg } from "@/lib/api";
import { isNetErr, sendPrint } from "@/lib/mobile";
import { printDoc, printErr } from "@/lib/btprint";
import { cn } from "@/lib/utils";

export function CartRow({ i, upd, setQty }) {
  return (
    <li className="flex items-start gap-2 p-3" data-testid={`m-cart-item-${i.product_id}`}>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{i.name}</p>
        <p className="font-mono text-xs text-slate-500">{brl(i.price)} cada</p>
        <input value={i.notes} onChange={(e) => upd(i.key, { notes: e.target.value })} placeholder="+ observação (ex.: sem cebola)"
          className="mt-1 w-full bg-transparent text-sm text-amber-700 outline-none placeholder:text-slate-400" data-testid={`m-cart-notes-${i.product_id}`} />
      </div>
      <div className="flex items-center gap-1.5">
        <button onClick={() => setQty(i.key, i.qty - 1)} className="grid h-9 w-9 place-items-center rounded-full border border-slate-300" data-testid={`m-cart-dec-${i.product_id}`}>{i.qty === 1 ? <Trash2 className="h-4 w-4 text-red-500" /> : <Minus className="h-4 w-4" />}</button>
        <span className="w-6 text-center font-mono font-bold" data-testid={`m-cart-qty-${i.product_id}`}>{i.qty}</span>
        <button onClick={() => setQty(i.key, i.qty + 1)} className="grid h-9 w-9 place-items-center rounded-full bg-[#1565c0] text-white" data-testid={`m-cart-inc-${i.product_id}`}><Plus className="h-4 w-4" /></button>
      </div>
    </li>
  );
}

export default function MCart() {
  const { id } = useParams();
  const { c, cid } = useComanda(id, 0);
  const { cart, setCart, total, count } = useCartActions(cid);
  const { enqueue } = useMobile();
  const { session } = useAuth();
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const upd = (key, patch) => setCart((cs) => cs.map((i) => (i.key === key ? { ...i, ...patch } : i)));
  const setQty = (key, qty) => (qty <= 0 ? setCart((cs) => cs.filter((i) => i.key !== key)) : upd(key, { qty }));

  const send = async () => {
    const items = cart.map((i) => ({ product_id: i.product_id, qty: i.qty, notes: i.notes, options: i.options }));
    setBusy(true);
    try {
      if (cid.startsWith("off-")) throw new Error("offline");
      const { data } = await api.post(`/comandas/${cid}/items`, { items });
      setCart([]);
      const o = data.last_order;
      if (o.items.length || o.sectors.length) {
        sendPrint("order", `Pedido ${o.number} · ${o.label}`, { order: o })
          .then(() => toast.success(`Pedido ${o.number} enviado para a produção e impressora`))
          .catch(() => toast.warning(`Pedido ${o.number} enviado, mas a impressão não foi enviada`));
      } else toast.success(`Pedido ${o.number} enviado`);
      if (o.bluetooth?.length) printDoc(session, "order", `Pedido ${o.number}`, { order: o }, { quiet: true }).catch((e) => toast.error(printErr(e)));
      nav("/m/pdv", { replace: true });
    } catch (e) {
      if (e.message !== "offline" && !isNetErr(e)) { toast.error(errMsg(e)); return; }
      enqueue({ type: "items", comanda_id: cid, payload: { items }, label: `Comanda #${c?.number ?? ""}`, total, lines: cart.map((i) => `${i.qty}x ${i.name}`) });
      setCart([]);
      toast.warning("Sem internet: o pedido ficou guardado no celular e será enviado quando a conexão voltar");
      nav("/m/pdv", { replace: true });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <MTopBar title={`Carrinho (${count})`} back={`/m/comanda/${id}/produtos`} />
      {c && <ComandaBar c={c} />}
      <MPage>
        {!cart.length ? <EmptyArt icon={ShoppingCart} title="Carrinho vazio" text="Volte para os produtos e toque nos itens para montar o pedido." testId="m-cart-empty" /> : (
          <ul className={cn(cardCls, "divide-y divide-slate-100")} data-testid="m-cart-list">
            {cart.map((i) => <CartRow key={i.key} i={i} upd={upd} setQty={setQty} />)}
          </ul>
        )}
        <Button asChild variant="outline" className="h-14 w-full rounded-xl border-slate-300 bg-white text-base font-semibold text-slate-700 hover:bg-slate-50">
          <Link to={`/m/comanda/${id}/produtos`} data-testid="m-cart-add-more"><Plus className="h-5 w-5" />Adicionar mais itens</Link>
        </Button>
      </MPage>
      <Footer className="border-t bg-white px-4 py-3 shadow-[0_-8px_24px_rgba(15,23,42,0.08)]" testId="m-cart-send-footer">
        <Button onClick={send} disabled={!cart.length || busy} className="h-14 w-full justify-between rounded-xl bg-[#16a34a] px-5 text-base text-white hover:bg-[#15803d]" data-testid="m-cart-send-btn">
          <span className="flex items-center gap-2">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}Enviar pedido</span>
          <span className="font-mono text-lg font-bold">{brl(total)}</span>
        </Button>
      </Footer>
    </>
  );
}
